import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';

import { ErrorResponse } from '../../../custom-fetch';
import { Route } from '../../../routes';
import { QueryKey } from '../../../services/query-keys';
import { useHeaderLinksService } from '../../../services/header-links-service';
import { IHeaderLink } from '../../../shared/IHeaderLink';
import ErrorsAlert from '../../ErrorsAlert/ErrorsAlert';
import Spinner from '../../ui/Spinner/Spinner';
import Table from '../../ui/Table/Table';
import { useState, useEffect } from 'react';

const HeaderLinksList: React.FC = () => {
  const { fetchHeaderLinks, saveHeaderLinks } = useHeaderLinksService();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    data: initialLinks,
    isPending,
    isError,
    error,
  } = useQuery<IHeaderLink[], ErrorResponse>({
    queryKey: [QueryKey.headerLinks],
    queryFn: fetchHeaderLinks,
  });

  const [links, setLinks] = useState<IHeaderLink[]>([]);

  useEffect(() => {
    if (initialLinks) {
      setLinks([...initialLinks].sort((a, b) => a.ordering - b.ordering));
    }
  }, [initialLinks]);

  const saveMutation = useMutation({
    mutationFn: saveHeaderLinks,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.headerLinks] });
    },
  });

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(links);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedItems = items.map((item, index) => ({
      ...item,
      ordering: index,
    }));

    setLinks(updatedItems);
    saveMutation.mutate(updatedItems);
  };

  if (isError) {
    return <ErrorsAlert errors={error.messages} />;
  }

  if (isPending) {
    return <Spinner />;
  }

  const openHandler = (id: string) => {
    navigate(Route.headerLink(id));
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="header-links">
        {(provided) => (
          <Table hover>
            <thead>
              <tr>
                <th style={{ width: '40px' }}></th>
                <th>Text</th>
                <th>Link</th>
                <th>Visible</th>
                <th>Sublinks</th>
              </tr>
            </thead>
            <tbody {...provided.droppableProps} ref={provided.innerRef}>
              {links.map((link, index) => (
                <Draggable key={link.id} draggableId={link.id!} index={index}>
                  {(provided, snapshot) => (
                    <tr
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{
                        ...provided.draggableProps.style,
                        background: snapshot.isDragging ? '#f0f0f0' : 'transparent',
                        cursor: 'pointer'
                      }}
                      onClick={() => openHandler(link.id!)}
                    >
                      <td {...provided.dragHandleProps} onClick={(e) => e.stopPropagation()}>
                        <span style={{ fontSize: '20px', color: '#999' }}>☰</span>
                      </td>
                      <td>{link.text}</td>
                      <td>{link.link || '-'}</td>
                      <td>{link.isVisible ? 'Yes' : 'No'}</td>
                      <td>{link.sublinks?.length || 0}</td>
                    </tr>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </tbody>
          </Table>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default HeaderLinksList;
